<template>
  <div
    class="chat-container"
    :style="containerStyle"
  >
    <Transition name="fade">
      <div
        v-show="!isChatOpen"
        key="icon"
        class="chat-icon"
        @click="openChat"
      >
        <AuIcon
          icon="ai_chat"
          class="ai_chat"
          @click="openChat"
        />
      </div>
    </Transition>

    <Transition name="slide">
      <div
        v-show="isChatOpen"
        key="window"
        class="chat-window"
        :class="{ 'full-width': isMobile }"
      >
        <div class="chat-header">
          <span
            class="title"
            @click="openCarrot"
          >{{ $t('aiChat.title') }}</span>
          <button
            class="close-btn"
            @click="closeChat"
          >
            <AuIcon
              icon="close_round"
              class="close_chat"
              @click="closeChat"
            />
          </button>
        </div>
        <div class="chat-messages">
          <span class="today">{{ today }}</span>
          <div
            v-for="(message, index) in messages"
            :key="index"
            class="message-container"
            :class="{ 'user-message-container': message.isUser }"
          >
            <div
              class="message"
              :class="{ 'user-message': message.isUser }"
            >
              {{ message.text }}
            </div>
            <span
              class="message-time"
              :class="{ 'user-time': message.isUser }"
            >
              {{ message.time }}
            </span>
          </div>
          <div
            v-if="isWaitingForResponse"
            class="message typing-indicator"
          >
            <img
              src="/svg/light/waiting.svg"
              alt="Typing indicator"
              class="typing-dots"
              width="14"
              height="10"
            >
          </div>
        </div>
        <div class="chat-input">
          <input
            v-model="inputMessage"
            :placeholder="$t('aiChat.placeholder')"
            @keyup.enter="visualMessage"
          >
          <AuButton
            class="send-btn"
            border-radius="50"
            type="primary"
            center
            :disabled="isWaitingForResponse"
            @click="visualMessage"
          >
            {{ $t('aiChat.send') }}
          </AuButton>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script>
import moment from "moment/moment";
import { mapActions, mapGetters } from "vuex";

export default {
  data() {
    return {
      isChatOpen: false,
      isWaitingForResponse: false,
      inputMessage: "",
      messages: [],
      dateHistory: null,
      rightOffset: "24px",
      bottomOffset: "100px",
      isMobile: false
    };
  },
  computed: {
    ...mapGetters({
      language: "language"
    }),
    containerStyle() {
      return {
        right: this.rightOffset,
        bottom: this.bottomOffset
      };
    },

    today() {
      const dateToFormat = this.dateHistory ? moment(this.dateHistory) : moment();
      return dateToFormat.locale(this.language).format("DD MMM");
    }
  },

  watch: {
    messages: {
      handler() {
        this.scrollToBottom();
      },
      deep: true
    }
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.checkScreenSize);
  },

  async mounted() {
    await this.getMessage();
  },

  methods: {
    ...mapActions({
      sendAiChatBot: "sendAiChatBot",
      getAiChatBot: "getAiChatBot"
    }),

    openCarrot() {
      window.carrotquest.open();
      this.closeChat();
    },

    openChat() {
      this.isChatOpen = true;
      this.checkScreenSize();
      window.addEventListener("resize", this.checkScreenSize);
      this.scrollToBottom();
    },
    closeChat() {
      this.isChatOpen = false;
      window.removeEventListener("resize", this.checkScreenSize);
    },

    async visualMessage() {
      if (this.inputMessage.trim()) {
        this.messages.push({
          time: moment().format("hh:mm"),
          text: this.inputMessage,
          isUser: true
        });
        await this.sendMessage();
      }
    },

    async getMessage() {
      const params = {
        page: 1,
        itemsPerPage: 10
      };

      const { data } = await this.getAiChatBot(params);

      this.dateHistory = data[0]?.createdAt;
      this.messages = [];

      // Перебираем для истории сообщений
      data.forEach(item => {
        // сообщения пользователя (message)
        if (item.message) {
          this.messages.push({
            time: moment(item.createdAt).format("hh:mm"),
            text: item.message,
            isUser: true
          });
        }

        // сообщения бота (answer)
        if (item.answer) {
          this.messages.push({
            time: moment(item.createdAt).format("hh:mm"),
            text: item.answer,
            isUser: false
          });
        }
      });
    },


    async sendMessage(retryCount = 0) {
      const maxRetries = 4;
      let resp;

      this.isWaitingForResponse = true;
      const params = {
        question: this.inputMessage
      };

      try {
        resp = await this.sendAiChatBot(params);

        if (resp.status !== 200) {
          this.messages.push({
            time: moment().format("hh:mm"),
            text: this.$t("aiChat.error_1"),
            isUser: false
          });
        }
        else {
          this.messages.push({
            time: moment().format("hh:mm"),
            text: resp?.data?.answer || this.$t("aiChat.error_2"),
            isUser: false
          });
          this.inputMessage = "";
        }
      }
      catch (error) {
        if (error.response?.status === 502 && retryCount < maxRetries) {
          const nextRetryCount = retryCount + 1;
          await new Promise(resolve => setTimeout(resolve, 5500));
          await this.sendMessage(nextRetryCount);
          return;
        }

        this.messages.push({
          time: moment().format("hh:mm"),
          text: this.$t("aiChat.error_2"),
          isUser: false
        });
      }
      finally {
        this.isWaitingForResponse = false;
      }
    },

    checkScreenSize() {
      this.isMobile = window.innerWidth <= 420;
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const messagesContainer = this.$el.querySelector(".chat-messages");
        if (messagesContainer) {
          messagesContainer.scrollTop = messagesContainer.scrollHeight;
        }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.chat-container {
  position: fixed;
  z-index: 9997;

  .chat-icon {
    width: 60px;
    height: 60px;
    background-color: #ccc;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;

    &:hover {
      background-color: #bbb;
    }

    .ai_chat{
      margin-top: 2px;
    }
  }

  .chat-window {
    width: 370px;
    height: 500px;
    background-color: #fff;
    border-radius: 10px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
    display: flex;
    flex-direction: column;
    overflow: hidden;

    &.full-width {
      width: calc(100vw - 40px);
      height: calc(100vh - 100px);
      border-radius: 0;
    }

    .chat-header {
      background-color: #f5f5f5;
      padding: 15px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-bottom: 1px solid #e0e0e0;
      color: #080808;
      font-family: 'Inter', Arial, sans-serif;

      .title {
        cursor: pointer;
      }

      span {
        font-weight: bold;
        font-size: 14px;
      }

      .close-btn {
        background: none;
        border: none;
        cursor: pointer;
      }
    }

  .chat-messages {
    flex: 1;
    padding: 15px;
    overflow-y: auto;
    background-color: #f9f9f9;
    color: #080808;

    .message-container {
      display: flex;
      align-items: flex-end;
      margin-bottom: 10px;
      max-width: 80%;
      margin-right: auto;

      &.user-message-container {
        margin-left: auto;
        margin-right: 0;
        flex-direction: row-reverse;
      }

      .message-time {
        font-family: 'Inter', Arial, sans-serif;
        font-size: 10px;
        font-weight: 100;
        color: #999;
        margin: 0 6px;
        white-space: nowrap;
        align-self: flex-end;
      }

      .message {
        font-family: 'Inter', Arial, sans-serif;
        font-size: 14px;
        font-weight: 100;
        padding: 8px 12px;
        border-radius: 15px;
        background-color: #e3f2fd;
        border-bottom-left-radius: 5px;

        &.user-message {
          background-color: #b9f3db;
          border-bottom-right-radius: 5px;
          border-bottom-left-radius: 15px;
        }
      }
    }
  }

    .chat-input {
      display: flex;
      padding: 10px;
      border-top: 1px solid #e0e0e0;
      background-color: #fff;

      input {
        flex: 1;
        padding: 10px;
        padding-left: 16px;
        border: 1px solid #ddd;
        border-radius: 20px;
        outline: none;
        margin-right: 10px;
        color: #080808;
        font-family: 'Inter', Arial, sans-serif;
        font-size: 14px;
        font-weight: 100;

        &:focus {
          border-color: #8adcba;
        }
      }

      .send-btn {
        padding: 10px 15px;
        background-color: #2aba7c;
        color: white;
        border: none;
        border-radius: 20px;
        cursor: pointer;
        transition: background-color 0.3s;

        &:hover {
          background-color: #8adcba;
        }
      }
    }
  }
}

.typing-indicator {
  background-color: #e3f2fd;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 15px;
  max-width: 80px !important;
  margin-bottom: 10px;
  border-bottom-left-radius: 5px;

  span {
    color: green;
    font-size: 12px;
    color: #999;
  }
}

.typing-dots {
  height: 10px;

  circle {
    opacity: 0;
  }
}

.today {
  display: flex;
  justify-content: center;
  color: #999;
  font-size: 12px;
  font-family: 'Inter', Arial, sans-serif;
  font-weight: 100;
  margin-bottom: 10px;
}

// Анимация чата
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

.slide-enter-active, .slide-leave-active {
  transition: all 0.3s ease;
}
.slide-enter-from {
  transform: translateY(20px);
  opacity: 0;
}
.slide-leave-to {
  transform: translateY(20px);
  opacity: 0;
}

.chat-icon, .chat-window {
  position: absolute;
  bottom: 0;
  right: 0;
}
</style>
